import {
  FocusEventHandler,
  forwardRef,
  KeyboardEventHandler,
  PropsWithChildren,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { nodeDefs } from "src/nodes";
import { NodeInstance } from "src/types/node.types";
import { EditorAction } from "../types/editor-action.types";

export interface FocusableNodeProps {
  node: NodeInstance;
  focused?: boolean;
  className?: string;
  dispatch: (action: EditorAction) => void;
  scrollIntoView: () => void;
}

export const FocusableNode = forwardRef<
  HTMLDivElement,
  PropsWithChildren<FocusableNodeProps>
>((props, forwardedRef) => {
  const ref = useRef<HTMLDivElement>(null);

  const dispatch = props.dispatch;
  const nodeId = props.node.id;
  const scrollIntoView = props.scrollIntoView;

  const focusHandler = useCallback<FocusEventHandler<HTMLDivElement>>(
    (e) => {
      scrollIntoView();

      if (e.currentTarget === e.target) {
        dispatch({ type: "FOCUS_NODE", nodeId });
      }
    },
    [dispatch, nodeId, scrollIntoView]
  );

  const blurHandler = useCallback<FocusEventHandler>(
    (e) => {
      if (e.currentTarget !== e.target) return;
      dispatch({ type: "BLUR_NODE" });
    },
    [dispatch]
  );

  const onKeyDownHandler: KeyboardEventHandler<HTMLElement> = useCallback(
    (e) => {
      if (e.currentTarget !== e.target) return;
      if (e.key === "Backspace") {
        dispatch({ type: "DELETE_NODE", nodeId });
        return;
      }

      if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
        e.preventDefault();
        dispatch({ type: "FOCUS_PREV_NODE", nodeId });
        return;
      }

      if (e.key === "ArrowDown" || e.key === "ArrowRight") {
        e.preventDefault();
        dispatch({ type: "FOCUS_NEXT_NODE", nodeId });
        return;
      }

      if (e.key === "Escape") {
        e.currentTarget.blur();
        return;
      }
    },
    [dispatch, nodeId]
  );

  useImperativeHandle(forwardedRef, () => ref.current!);

  useEffect(() => {
    props.focused && ref.current?.focus({ preventScroll: true });
  }, [props.focused]);

  const nodeDef = nodeDefs[props.node.type];
  const label = props.node.name
    ? `Node ${props.node.name}`
    : `${nodeDef.displayName} node`;

  return (
    <div
      ref={ref}
      className={props.className}
      tabIndex={0}
      aria-label={label}
      onKeyDown={onKeyDownHandler}
      onFocus={focusHandler}
      onBlur={blurHandler}
    >
      {props.children}
    </div>
  );
});
